import React, { useState, useCallback } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';

import IconPlus from '../IconPlus/IconPlus';

import css from './SelectSingleFilterPlain.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

function SelectSingleFilterPlain({
  rootClassName = null,
  className = null,
  label,
  options,
  queryParamNames,
  initialValues = null,
  twoColumns = false,
  useHighlight = true,
  useBorder = false,
  onSelect,
}) {
  const [isOpen, setIsOpen] = useState(true);

  const selectOption = useCallback(
    (option, e) => {
      const queryParamName = getQueryParamName(queryParamNames);
      onSelect({ [queryParamName]: option });
      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
    },
    [onSelect, queryParamNames]
  );

  const toggleIsOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const queryParamName = getQueryParamName(queryParamNames);
  const initialValue =
    initialValues && initialValues[queryParamName] ? initialValues[queryParamName] : null;
  const labelClass = initialValue ? css.labelSelected : css.label;

  const optionsContainerClass = classNames({
    [css.optionsContainerOpen]: isOpen,
    [css.optionsContainerClosed]: !isOpen,
    [css.twoColumns]: twoColumns,
  });

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.filterHeader}>
        <button className={css.labelButton} onClick={toggleIsOpen}>
          <span className={css.labelButtonContent}>
            <span className={labelClass}>{label}</span>
            <span className={css.openSign}>
              <IconPlus isOpen={isOpen} isSelected={!!initialValue} />
            </span>
          </span>
        </button>
      </div>
      <div className={optionsContainerClass}>
        {options.map(option => {
          const selected = initialValue === option.key;
          const optionClass = classNames(css.option, {
            [css.optionHighlight]: selected && useHighlight,
          });
          const optionBorderClass = useBorder
            ? classNames({
                [css.optionBorderSelected]: selected,
                [css.optionBorder]: !selected,
              })
            : null;
          return (
            <button
              key={option.key}
              className={optionClass}
              onClick={e => selectOption(option.key, e)}
            >
              {useBorder ? <span className={optionBorderClass} /> : null}
              {option.label}
            </button>
          );
        })}
        <button className={css.clearButton} onClick={e => selectOption(null, e)}>
          <FormattedMessage id={'SelectSingleFilter.plainClear'} />
        </button>
      </div>
    </div>
  );
}



SelectSingleFilterPlain.propTypes = {
  rootClassName: string,
  className: string,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSelect: func.isRequired,

  options: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  initialValues: object,
  twoColumns: bool,
  useHighlight: bool,
  useBorder: bool,
};

export default SelectSingleFilterPlain;
